import { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckOutlined,
  DownOutlined,
  InfoCircleFilled,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import "./CampaignLocationsSelector.scss";

export type CampaignLocationOption = {
  uuid: string;
  name?: string;
  address?: string;
  addressDisplay?: string;
  cityState?: string;
  city?: string;
  state?: string;
  country?: string;
  disabled?: boolean;
};

type GroupedLocationOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type GroupedLocationCity = {
  label: string;
  key: string;
  options: GroupedLocationOption[];
};

type GroupedLocationRegion = {
  label: string;
  key: string;
  cities: GroupedLocationCity[];
};

type GroupedLocationOptions = GroupedLocationRegion[];

export type CampaignLocationsSelectorProps = {
  venues: CampaignLocationOption[];
  selectedVenueUuids: string[];
  lockedVenueUuids?: string[];
  onSelectVenue: (uuid: string) => void;
  onRemoveVenue: (uuid: string) => void;
  onCreateCustomVenue?: (input: string) => void;
  onClearSelection?: () => void;
  placeholder?: string;
  disabled?: boolean;
};

type NormalizedVenue = CampaignLocationOption & {
  label: string;
  searchableText: string;
  regionLabel: string;
  regionKey: string;
  cityLabel: string;
  cityKey: string;
};

const OTHER_GROUP_LABEL = "Other";
const US_STATE_NAME_TO_CODE: Record<string, string> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
  "district of columbia": "DC",
};
const US_STATE_CODE_SET = new Set(Object.values(US_STATE_NAME_TO_CODE));

const toTitleCase = (value: string): string => {
  return value
    .toLowerCase()
    .replace(/\b([a-z\u00c0-\u024f])/g, (match) => match.toUpperCase());
};

const US_STATE_CODE_TO_NAME: Record<string, string> = Object.entries(
  US_STATE_NAME_TO_CODE,
).reduce<Record<string, string>>((acc, [stateName, stateCode]) => {
  acc[stateCode] =
    stateName === "district of columbia"
      ? "District of Columbia"
      : toTitleCase(stateName);
  return acc;
}, {});

const normalizeCityToken = (rawValue: string): string => {
  if (!rawValue) return "";

  const withoutLeadingZip = rawValue.trim().replace(/^\d{3,}\s+/, "");
  const withoutTrailingZip = withoutLeadingZip.replace(/\s+\d{3,}$/, "");
  const normalized = withoutTrailingZip.replace(/\s+/g, " ").trim();

  if (!normalized) return "";
  return toTitleCase(normalized);
};

const isLikelyCountryToken = (rawValue: string): boolean => {
  if (!rawValue.trim()) return false;
  if (/\d/.test(rawValue)) return false;

  const cleaned = rawValue.replace(/[^a-zA-Z\s]/g, "").trim();
  return cleaned.length >= 4;
};

const normalizeCountry = (rawValue: string): string | null => {
  const value = rawValue.trim();
  if (!value) return null;

  const compact = value.toLowerCase().replace(/[^a-z]/g, "");
  if (
    compact === "us" ||
    compact === "usa" ||
    compact === "unitedstates" ||
    compact === "unitedstatesofamerica"
  ) {
    return "United States";
  }
  if (compact === "brasil") {
    return "Brazil";
  }

  if (!isLikelyCountryToken(value)) return null;
  return toTitleCase(value);
};

const tryParseStateCode = (
  rawCode: string,
  allowLowercaseTokens = false,
): string | null => {
  const trimmedCode = rawCode.trim();
  if (!trimmedCode) return null;

  if (!allowLowercaseTokens && trimmedCode !== trimmedCode.toUpperCase()) {
    return null;
  }

  const normalizedCode = trimmedCode.toUpperCase();
  if (!normalizedCode) return null;
  if (!/^[A-Z]{2}$/.test(normalizedCode)) return null;

  if (US_STATE_CODE_SET.has(normalizedCode)) {
    return normalizedCode;
  }

  return normalizedCode;
};

const parseStateFromSegment = (
  rawSegment: string,
  allowLowercaseTokens = false,
): { stateCode: string; cityFromSegment?: string } | null => {
  const segment = rawSegment.trim();
  if (!segment) return null;

  const normalizedStateName = segment
    .toLowerCase()
    .replace(/[0-9]/g, " ")
    .replace(/[^a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const stateFromName = US_STATE_NAME_TO_CODE[normalizedStateName];
  if (stateFromName) {
    return { stateCode: stateFromName };
  }

  const hyphenMatch = segment.match(
    /^(.*?)[-\u2013]\s*([a-zA-Z]{2})(?:\s+\d{3,}[\w-]*)?\s*$/,
  );
  if (hyphenMatch) {
    const stateCode = tryParseStateCode(hyphenMatch[2], allowLowercaseTokens);
    if (stateCode) {
      return {
        stateCode,
        cityFromSegment: normalizeCityToken(hyphenMatch[1] || ""),
      };
    }
  }

  const tokenOnlyMatch = segment.match(/^([a-zA-Z]{2})(?:\s+\d{3,}[\w-]*)?\s*$/);
  if (tokenOnlyMatch) {
    const stateCode = tryParseStateCode(
      tokenOnlyMatch[1],
      allowLowercaseTokens,
    );
    if (stateCode) {
      return { stateCode };
    }
  }

  const trailingTokenMatch = segment.match(
    /^(.+?)\s([a-zA-Z]{2})(?:\s+\d{3,}[\w-]*)?\s*$/,
  );
  if (trailingTokenMatch && !/\d/.test(trailingTokenMatch[1])) {
    const stateCode = tryParseStateCode(
      trailingTokenMatch[2],
      allowLowercaseTokens,
    );
    if (stateCode) {
      return {
        stateCode,
        cityFromSegment: normalizeCityToken(trailingTokenMatch[1] || ""),
      };
    }
  }

  return null;
};

const parseLocationFromText = (
  rawValue: string,
  options?: { allowLowercaseStateTokens?: boolean },
): { city: string; state?: string; country?: string } | null => {
  const allowLowercaseStateTokens = Boolean(
    options?.allowLowercaseStateTokens,
  );

  const parts = rawValue
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length < 2) return null;

  const lastPart = parts[parts.length - 1];
  const countryFromLastPart = normalizeCountry(lastPart);
  const segments = countryFromLastPart ? parts.slice(0, -1) : parts;

  for (let index = segments.length - 1; index >= 0; index -= 1) {
    const stateResult = parseStateFromSegment(
      segments[index],
      allowLowercaseStateTokens,
    );
    if (!stateResult) continue;

    const cityFromPreviousSegment =
      index > 0 ? normalizeCityToken(segments[index - 1] || "") : "";
    const city = stateResult.cityFromSegment || cityFromPreviousSegment;
    if (!city) continue;

    const country = countryFromLastPart || normalizeCountry(lastPart);
    if (country) {
      return { city, state: stateResult.stateCode, country };
    }

    return { city, state: stateResult.stateCode };
  }

  const country = countryFromLastPart || normalizeCountry(lastPart);
  const citySourceIndex = countryFromLastPart ? parts.length - 2 : parts.length - 1;
  const city = normalizeCityToken(parts[citySourceIndex] || "");

  if (country && city) {
    return { city, country };
  }

  return null;
};

const formatLocationGrouping = (location: {
  city: string;
  state?: string;
  country?: string;
}): { regionLabel: string; cityLabel: string } => {
  const city = normalizeCityToken(location.city);
  if (!city) {
    return {
      regionLabel: OTHER_GROUP_LABEL,
      cityLabel: OTHER_GROUP_LABEL,
    };
  }

  const country = location.country ? normalizeCountry(location.country) : null;

  if (location.state) {
    const stateResult = parseStateFromSegment(location.state, true);
    if (stateResult?.stateCode) {
      if (country && country !== "United States") {
        return {
          regionLabel: country,
          cityLabel: city,
        };
      }

      return {
        regionLabel:
          US_STATE_CODE_TO_NAME[stateResult.stateCode] || stateResult.stateCode,
        cityLabel: city,
      };
    }
  }

  if (country) {
    return {
      regionLabel: country,
      cityLabel: city,
    };
  }

  return {
    regionLabel: OTHER_GROUP_LABEL,
    cityLabel: city,
  };
};

const normalizeGroupingKey = (value: string): string => {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
};

const getVenueLabel = (venue: CampaignLocationOption): string => {
  const baseName = venue.name?.trim();
  const baseAddress = (venue.addressDisplay || venue.address || "").trim();

  if (baseName && baseAddress) {
    return `${baseName} - ${baseAddress}`;
  }

  if (baseName) {
    return baseName;
  }

  if (baseAddress) {
    return baseAddress;
  }

  return venue.uuid;
};

const getVenueGrouping = (
  venue: CampaignLocationOption,
): { regionLabel: string; cityLabel: string } => {
  const cityStateSource = (venue.cityState || "").trim();
  if (cityStateSource) {
    const parsedFromCityState = parseLocationFromText(cityStateSource, {
      allowLowercaseStateTokens: true,
    });
    if (parsedFromCityState) {
      return formatLocationGrouping(parsedFromCityState);
    }
  }

  const citySource = normalizeCityToken(venue.city || "");
  const stateSource = parseStateFromSegment(venue.state || "", true)?.stateCode;
  const countrySource = normalizeCountry(venue.country || "");
  if (citySource && stateSource) {
    if (countrySource && countrySource !== "United States") {
      return {
        regionLabel: countrySource,
        cityLabel: citySource,
      };
    }

    return {
      regionLabel: US_STATE_CODE_TO_NAME[stateSource] || stateSource,
      cityLabel: citySource,
    };
  }

  if (citySource && countrySource) {
    return {
      regionLabel: countrySource,
      cityLabel: citySource,
    };
  }

  const addressSource = (venue.addressDisplay || venue.address || "").trim();
  if (addressSource) {
    const parsedFromAddress = parseLocationFromText(addressSource);
    if (parsedFromAddress) {
      return formatLocationGrouping(parsedFromAddress);
    }
  }

  return {
    regionLabel: OTHER_GROUP_LABEL,
    cityLabel: OTHER_GROUP_LABEL,
  };
};

const normalizeVenue = (venue: CampaignLocationOption): NormalizedVenue => {
  const label = getVenueLabel(venue);
  const grouping = getVenueGrouping(venue);
  const regionLabel = grouping.regionLabel;
  const cityLabel = grouping.cityLabel;
  const regionKey = normalizeGroupingKey(regionLabel);
  const cityKey = normalizeGroupingKey(cityLabel);

  return {
    ...venue,
    label,
    regionLabel,
    regionKey,
    cityLabel,
    cityKey,
    searchableText: `${label} ${regionLabel} ${cityLabel}`.toLowerCase(),
  };
};

export const buildGroupedLocationOptions = (
  venues: CampaignLocationOption[],
  searchValue: string,
): GroupedLocationOptions => {
  const normalizedSearch = searchValue.trim().toLowerCase();
  const normalizedVenues = venues.map(normalizeVenue);

  const filtered = normalizedSearch
    ? normalizedVenues.filter((venue) =>
        venue.searchableText.includes(normalizedSearch),
      )
    : normalizedVenues;

  const grouped = filtered.reduce<
    Record<
      string,
      {
        label: string;
        key: string;
        cities: Record<
          string,
          {
            label: string;
            key: string;
            venues: NormalizedVenue[];
          }
        >;
      }
    >
  >(
    (acc, venue) => {
      const regionKey = venue.regionKey;
      if (!acc[regionKey]) {
        acc[regionKey] = {
          label: venue.regionLabel,
          key: regionKey,
          cities: {},
        };
      }

      const cityKey = venue.cityKey;
      if (!acc[regionKey].cities[cityKey]) {
        acc[regionKey].cities[cityKey] = {
          label: venue.cityLabel,
          key: cityKey,
          venues: [],
        };
      }

      acc[regionKey].cities[cityKey].venues.push(venue);
      return acc;
    },
    {},
  );

  return Object.values(grouped)
    .sort((left, right) => left.label.localeCompare(right.label))
    .map((region) => {
      return {
        label: region.label,
        key: region.key,
        cities: Object.values(region.cities)
          .sort((left, right) => left.label.localeCompare(right.label))
          .map((city) => ({
            label: city.label,
            key: city.key,
            options: [...city.venues]
              .sort((left, right) => left.label.localeCompare(right.label))
              .map((venue) => ({
                value: venue.uuid,
                label: venue.label,
                disabled: venue.disabled,
              })),
          })),
      };
    });
};

const getCityExpansionKey = (regionKey: string, cityKey: string): string => {
  return `${regionKey}::${cityKey}`;
};

const flattenRegionOptions = (
  region: GroupedLocationRegion,
): GroupedLocationOption[] => {
  return region.cities.flatMap((city) => city.options);
};

const CampaignLocationsSelector: React.FC<CampaignLocationsSelectorProps> = ({
  venues,
  selectedVenueUuids,
  lockedVenueUuids = [],
  onSelectVenue,
  onRemoveVenue,
  onCreateCustomVenue,
  onClearSelection,
  placeholder = "Search for brand locations...",
  disabled,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [expandedRegions, setExpandedRegions] = useState<string[]>([]);
  const [expandedCities, setExpandedCities] = useState<string[]>([]);
  const [isListOpen, setIsListOpen] = useState<boolean>(false);

  const groupedOptions = useMemo(() => {
    return buildGroupedLocationOptions(venues, searchValue);
  }, [venues, searchValue]);

  const lockedSet = useMemo(() => {
    return new Set(lockedVenueUuids);
  }, [lockedVenueUuids]);

  const selectableVenueMap = useMemo(() => {
    return venues.reduce<Record<string, CampaignLocationOption>>((acc, venue) => {
      acc[venue.uuid] = venue;
      return acc;
    }, {});
  }, [venues]);

  const selectedVenueList = useMemo(() => {
    return selectedVenueUuids
      .map((uuid) => selectableVenueMap[uuid])
      .filter((venue): venue is CampaignLocationOption => Boolean(venue));
  }, [selectedVenueUuids, selectableVenueMap]);

  const selectedVenueSet = useMemo(() => {
    return new Set(selectedVenueUuids);
  }, [selectedVenueUuids]);

  const hasSelectableSearchResult = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!normalizedSearch) {
      return false;
    }

    return venues.some((venue) => {
      const label = getVenueLabel(venue).toLowerCase();
      return label === normalizedSearch;
    });
  }, [searchValue, venues]);

  const removableSelectedCount = selectedVenueUuids.filter(
    (uuid) => !lockedSet.has(uuid),
  ).length;

  const handleCreateCustomVenue = () => {
    if (!onCreateCustomVenue) return;

    const normalized = searchValue.trim();
    if (!normalized) return;
    if (hasSelectableSearchResult) return;

    onCreateCustomVenue(normalized);
    setSearchValue("");
  };

  const filteredRegionKeys = useMemo(() => {
    return groupedOptions.map((region) => region.key);
  }, [groupedOptions]);

  const filteredCityExpansionKeys = useMemo(() => {
    return groupedOptions.flatMap((region) =>
      region.cities.map((city) => getCityExpansionKey(region.key, city.key)),
    );
  }, [groupedOptions]);

  useEffect(() => {
    const hasActiveSearch = searchValue.trim().length > 0;

    if (hasActiveSearch) {
      setExpandedRegions(filteredRegionKeys);
      setExpandedCities(filteredCityExpansionKeys);
      return;
    }

    setExpandedRegions((previous) => {
      const sanitized = previous.filter((regionKey) =>
        filteredRegionKeys.includes(regionKey),
      );

      if (sanitized.length > 0) {
        return sanitized;
      }

      const withSelected = groupedOptions
        .filter((region) =>
          flattenRegionOptions(region).some((option) =>
            selectedVenueSet.has(option.value),
          ),
        )
        .map((region) => region.key);

      if (withSelected.length > 0) {
        return withSelected;
      }

      return filteredRegionKeys.length > 0 ? [filteredRegionKeys[0]] : [];
    });

    setExpandedCities((previous) => {
      const sanitized = previous.filter((cityKey) =>
        filteredCityExpansionKeys.includes(cityKey),
      );

      if (sanitized.length > 0) {
        return sanitized;
      }

      const withSelected = groupedOptions.flatMap((region) =>
        region.cities
          .filter((city) =>
            city.options.some((option) => selectedVenueSet.has(option.value)),
          )
          .map((city) => getCityExpansionKey(region.key, city.key)),
      );

      if (withSelected.length > 0) {
        return withSelected;
      }

      const firstRegion = groupedOptions[0];
      const firstCity = firstRegion?.cities[0];
      if (!firstRegion || !firstCity) return [];

      return [getCityExpansionKey(firstRegion.key, firstCity.key)];
    });
  }, [
    filteredCityExpansionKeys,
    filteredRegionKeys,
    groupedOptions,
    searchValue,
    selectedVenueSet,
  ]);

  useEffect(() => {
    if (!isListOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node | null;
      if (!targetNode) return;
      if (rootRef.current?.contains(targetNode)) return;
      setIsListOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isListOpen]);

  const handleToggleRegion = (regionKey: string) => {
    if (disabled) return;

    setExpandedRegions((previous) => {
      if (previous.includes(regionKey)) {
        return previous.filter((item) => item !== regionKey);
      }

      return [...previous, regionKey];
    });
  };

  const handleToggleCity = (regionKey: string, cityKey: string) => {
    if (disabled) return;

    const expansionKey = getCityExpansionKey(regionKey, cityKey);
    setExpandedCities((previous) => {
      if (previous.includes(expansionKey)) {
        return previous.filter((item) => item !== expansionKey);
      }

      return [...previous, expansionKey];
    });
  };

  const handleToggleLocation = (uuid: string) => {
    if (disabled) return;

    const venue = selectableVenueMap[uuid];
    if (!venue) return;
    if (venue.disabled) return;
    if (lockedSet.has(uuid)) return;

    if (selectedVenueSet.has(uuid)) {
      onRemoveVenue(uuid);
      return;
    }

    onSelectVenue(uuid);
  };

  const handleToggleRegionSelection = (region: GroupedLocationRegion) => {
    if (disabled) return;

    const regionOptions = flattenRegionOptions(region);
    const regionSelectedCount = regionOptions.filter((option) =>
      selectedVenueSet.has(option.value),
    ).length;
    const totalRegionCount = regionOptions.length;
    const shouldSelectRegion = regionSelectedCount < totalRegionCount;

    regionOptions.forEach((option) => {
      const uuid = option.value;
      if (option.disabled) return;
      if (lockedSet.has(uuid)) return;

      const isSelected = selectedVenueSet.has(uuid);

      if (shouldSelectRegion && !isSelected) {
        onSelectVenue(uuid);
      }

      if (!shouldSelectRegion && isSelected) {
        onRemoveVenue(uuid);
      }
    });
  };

  const handleToggleCitySelection = (city: GroupedLocationCity) => {
    if (disabled) return;

    const citySelectedCount = city.options.filter((option) =>
      selectedVenueSet.has(option.value),
    ).length;
    const totalCityCount = city.options.length;
    const shouldSelectCity = citySelectedCount < totalCityCount;

    city.options.forEach((option) => {
      const uuid = option.value;
      if (option.disabled) return;
      if (lockedSet.has(uuid)) return;

      const isSelected = selectedVenueSet.has(uuid);

      if (shouldSelectCity && !isSelected) {
        onSelectVenue(uuid);
      }

      if (!shouldSelectCity && isSelected) {
        onRemoveVenue(uuid);
      }
    });
  };

  const hasLockedSelections = lockedVenueUuids.length > 0;

  return (
    <div className="campaign-locations-selector" ref={rootRef}>
      <div className="campaign-locations-selector__search-shell">
        <SearchOutlined className="campaign-locations-selector__search-icon" />
        <input
          type="text"
          value={searchValue}
          placeholder={placeholder}
          className="campaign-locations-selector__search-input"
          onFocus={() => setIsListOpen(true)}
          onChange={(event) => {
            setSearchValue(event.target.value);
            setIsListOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setIsListOpen(false);
              return;
            }
            if (event.key === "Enter") {
              handleCreateCustomVenue();
            }
          }}
          disabled={disabled}
        />
      </div>

      <div className="campaign-locations-selector__selected-box">
        <div className="campaign-locations-selector__selected-title">
          Selected ({selectedVenueList.length})
        </div>
        <div className="campaign-locations-selector__chips-shell">
          <div className="campaign-locations-selector__chips">
            {selectedVenueList.map((venue) => {
              const isLocked = lockedSet.has(venue.uuid);
              const label = getVenueLabel(venue);

              return (
                <div
                  key={venue.uuid}
                  className={`campaign-locations-selector__chip ${
                    isLocked ? "is-locked" : ""
                  }`}
                >
                  <span className="campaign-locations-selector__chip-label">
                    {label}
                  </span>
                  {isLocked ? (
                    <span className="campaign-locations-selector__chip-lock">
                      current
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="campaign-locations-selector__chip-remove"
                      onClick={() => onRemoveVenue(venue.uuid)}
                      aria-label={`Remove ${label}`}
                    >
                      x
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {onClearSelection && removableSelectedCount > 0 ? (
            <button
              type="button"
              onClick={onClearSelection}
              className="campaign-locations-selector__clear"
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {isListOpen ? (
        <>
          <div className="campaign-locations-selector__groups">
            {groupedOptions.map((region) => {
              const regionOptions = flattenRegionOptions(region);
              const isRegionExpanded = expandedRegions.includes(region.key);
              const regionSelectedCount = regionOptions.filter((option) =>
                selectedVenueSet.has(option.value),
              ).length;
              const regionTotalCount = regionOptions.length;
              const regionHasSelectable = regionOptions.some((option) => {
                if (option.disabled) return false;
                if (lockedSet.has(option.value)) return false;
                return true;
              });
              const regionIsChecked =
                regionTotalCount > 0 && regionSelectedCount === regionTotalCount;
              const regionIsIndeterminate =
                regionSelectedCount > 0 && regionSelectedCount < regionTotalCount;

              return (
                <div
                  key={region.key}
                  className="campaign-locations-selector__group"
                >
                  <div className="campaign-locations-selector__group-header">
                    <input
                      type="checkbox"
                      checked={regionIsChecked}
                      disabled={!regionHasSelectable || disabled}
                      onChange={() => handleToggleRegionSelection(region)}
                      aria-label={`Select region ${region.label}`}
                      ref={(input) => {
                        if (!input) return;
                        input.indeterminate = regionIsIndeterminate;
                      }}
                    />
                    <button
                      type="button"
                      className="campaign-locations-selector__group-toggle"
                      onClick={() => handleToggleRegion(region.key)}
                      disabled={disabled}
                    >
                      <span className="campaign-locations-selector__group-title">
                        {region.label}
                      </span>
                      <span className="campaign-locations-selector__group-count">
                        · {regionSelectedCount}/{regionTotalCount} selected
                      </span>
                      <span className="campaign-locations-selector__group-chevron">
                        {isRegionExpanded ? <DownOutlined /> : <RightOutlined />}
                      </span>
                    </button>
                  </div>

                  {isRegionExpanded ? (
                    <div className="campaign-locations-selector__region-cities">
                      {region.cities.map((city) => {
                        const cityExpansionKey = getCityExpansionKey(
                          region.key,
                          city.key,
                        );
                        const isCityExpanded =
                          expandedCities.includes(cityExpansionKey);
                        const citySelectedCount = city.options.filter((option) =>
                          selectedVenueSet.has(option.value),
                        ).length;
                        const cityTotalCount = city.options.length;
                        const cityHasSelectable = city.options.some((option) => {
                          if (option.disabled) return false;
                          if (lockedSet.has(option.value)) return false;
                          return true;
                        });
                        const cityIsChecked =
                          cityTotalCount > 0 && citySelectedCount === cityTotalCount;
                        const cityIsIndeterminate =
                          citySelectedCount > 0 && citySelectedCount < cityTotalCount;

                        return (
                          <div
                            key={cityExpansionKey}
                            className="campaign-locations-selector__city"
                          >
                            <div className="campaign-locations-selector__city-header">
                              <input
                                type="checkbox"
                                checked={cityIsChecked}
                                disabled={!cityHasSelectable || disabled}
                                onChange={() => handleToggleCitySelection(city)}
                                aria-label={`Select city ${city.label} in ${region.label}`}
                                ref={(input) => {
                                  if (!input) return;
                                  input.indeterminate = cityIsIndeterminate;
                                }}
                              />
                              <button
                                type="button"
                                className="campaign-locations-selector__city-toggle"
                                onClick={() => handleToggleCity(region.key, city.key)}
                                disabled={disabled}
                              >
                                <span className="campaign-locations-selector__city-title">
                                  {city.label}
                                </span>
                                <span className="campaign-locations-selector__city-count">
                                  · {citySelectedCount}/{cityTotalCount} selected
                                </span>
                                <span className="campaign-locations-selector__group-chevron">
                                  {isCityExpanded ? (
                                    <DownOutlined />
                                  ) : (
                                    <RightOutlined />
                                  )}
                                </span>
                              </button>
                            </div>

                            {isCityExpanded ? (
                              <div className="campaign-locations-selector__group-items">
                                {city.options.map((option) => {
                                  const venue = selectableVenueMap[option.value];
                                  if (!venue) return null;

                                  const isLocked = lockedSet.has(option.value);
                                  const isDisabled =
                                    Boolean(option.disabled) || isLocked;
                                  const isChecked = selectedVenueSet.has(option.value);

                                  return (
                                    <button
                                      type="button"
                                      key={option.value}
                                      className={`campaign-locations-selector__item ${
                                        isDisabled ? "is-disabled" : ""
                                      }`}
                                      onClick={() =>
                                        handleToggleLocation(option.value)
                                      }
                                      disabled={disabled}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={isDisabled}
                                        readOnly
                                      />
                                      <div className="campaign-locations-selector__item-copy">
                                        <div className="campaign-locations-selector__item-label">
                                          {venue.name || option.label}
                                        </div>
                                        {venue.addressDisplay || venue.address ? (
                                          <div className="campaign-locations-selector__item-address">
                                            {venue.addressDisplay || venue.address}
                                          </div>
                                        ) : null}
                                        {isLocked ? (
                                          <span className="campaign-locations-selector__item-current">
                                            current
                                          </span>
                                        ) : null}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {onCreateCustomVenue && searchValue && !hasSelectableSearchResult ? (
            <button
              type="button"
              className="campaign-locations-selector__create"
              onClick={handleCreateCustomVenue}
            >
              <CheckOutlined />
              Press Enter to add "{searchValue.trim()}"
            </button>
          ) : null}
        </>
      ) : null}

      {hasLockedSelections ? (
        <div className="campaign-locations-selector__info">
          <InfoCircleFilled />
          <span>Current locations can&apos;t be removed here.</span>
        </div>
      ) : null}
    </div>
  );
};

export default CampaignLocationsSelector;
